import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Container, Form, Row, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { BsArrowLeft, BsPencil, BsPlus, BsToggleOff, BsToggleOn } from "react-icons/bs";
import Cabecalho from "../../../components/Cabecalho/Cabecalho";
import api from "../../../Utils/api";
import "./naturezas.css";

const vazio = { nome: "" };

function normalizar(nome) {
  return String(nome || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .trim().replace(/\s+/g, " ").toLocaleLowerCase("pt-BR");
}

export default function Naturezas() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [pendentes, setPendentes] = useState(0);
  const [grafiasPendentes, setGrafiasPendentes] = useState([]);
  const [form, setForm] = useState(vazio);
  const [editingId, setEditingId] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/naturezas", { params: { incluirInativas: true, q: search || undefined } });
      setItems(data.items || []);
      setPendentes(data.pendentes || 0);
      setGrafiasPendentes(data.grafiasPendentes || []);
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Não foi possível carregar as naturezas.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const possibleDuplicate = useMemo(() => {
    const key = normalizar(form.nome);
    return key && items.some((item) => item._id !== editingId && item.normalizado === key);
  }, [editingId, form.nome, items]);

  const updateField = (field) => (event) =>
    setForm((current) => ({ ...current, [field]: event.target.value }));

  const startEdit = (item) => {
    setEditingId(item._id);
    setForm({ nome: item.nome });
    setError("");
    setNotice("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const clearForm = () => {
    setEditingId("");
    setForm(vazio);
    setError("");
  };

  const save = async (event) => {
    event.preventDefault();
    setError("");
    setNotice("");
    if (possibleDuplicate) {
      setError("Já existe uma natureza com esse nome.");
      return;
    }
    const payload = { nome: form.nome.trim() };
    setSaving(true);
    try {
      if (editingId) await api.put(`/naturezas/${editingId}`, payload);
      else await api.post("/naturezas", payload);
      setNotice(editingId ? "Natureza atualizada." : "Natureza cadastrada.");
      clearForm();
      await load();
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Não foi possível salvar a natureza.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (item) => {
    setError("");
    setNotice("");
    try {
      await api.patch(`/naturezas/${item._id}/status`, { ativo: !item.ativo });
      setNotice(`Natureza ${item.ativo ? "desativada" : "ativada"}.`);
      await load();
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Não foi possível alterar o status.");
    }
  };

  return (
    <>
      <Cabecalho />
      <main className="naturezas-page">
        <Container>
          <div className="naturezas-heading">
            <div>
              <p className="dashboard-eyebrow">Administração</p>
              <h2>Catálogo de naturezas</h2>
              <p>Cada nome aparece uma única vez no catálogo. A duplicidade ignora acentos e diferenças entre maiúsculas e minúsculas.</p>
            </div>
            <Button variant="outline-secondary" onClick={() => navigate("/dashboard")}>
              <BsArrowLeft className="me-2" /> Voltar
            </Button>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}
          {notice && <Alert variant="success">{notice}</Alert>}
          <Alert variant="info">
            {pendentes} boletim(ns) ainda aguardam associação a uma natureza oficial. Naturezas utilizadas não podem ser apagadas; desative-as para impedir novas seleções.
          </Alert>
          {grafiasPendentes.length > 0 && (
            <section className="naturezas-card mb-4" aria-labelledby="naturezas-pendentes">
              <h3 id="naturezas-pendentes">Grafias históricas pendentes</h3>
              <Table responsive size="sm" className="mb-0">
                <thead><tr><th>Natureza legada</th><th>Boletins</th></tr></thead>
                <tbody>
                  {grafiasPendentes.map((item) => (
                    <tr key={item.natureza}>
                      <td>{item.natureza || "Natureza não informada"}</td>
                      <td>{item.ocorrencias}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </section>
          )}

          <section className="naturezas-card">
            <h3>{editingId ? "Editar natureza" : "Cadastrar natureza"}</h3>
            <Form onSubmit={save}>
              <Row className="g-3">
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label>Nome</Form.Label>
                    <Form.Control required maxLength={500} value={form.nome} onChange={updateField("nome")} />
                  </Form.Group>
                </Col>
              </Row>
              {possibleDuplicate && <Alert variant="warning" className="mt-3 mb-0">Possível duplicidade: já existe uma natureza com esse nome normalizado.</Alert>}
              <div className="naturezas-form-actions">
                <Button type="submit" disabled={saving || possibleDuplicate}>
                  {editingId ? "Salvar alterações" : <><BsPlus className="me-1" /> Cadastrar</>}
                </Button>
                {editingId && <Button type="button" variant="outline-secondary" onClick={clearForm}>Cancelar edição</Button>}
              </div>
            </Form>
          </section>

          <section className="naturezas-card mt-4">
            <div className="naturezas-list-heading">
              <h3>Naturezas cadastradas</h3>
              <Form.Control
                type="search"
                aria-label="Pesquisar naturezas"
                placeholder="Pesquisar por nome"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            {loading ? <p role="status">Carregando catálogo...</p> : (
              <Table responsive hover className="mb-0 align-middle">
                <thead><tr><th>Nome</th><th>Status</th><th>Boletins</th><th>Ações</th></tr></thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item._id}>
                      <td>
                        <strong>{item.nome}</strong>
                      </td>
                      <td>{item.ativo ? "Ativa" : "Inativa"}</td>
                      <td>{item.ocorrencias || 0}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button size="sm" variant="outline-primary" aria-label={`Editar ${item.nome}`} onClick={() => startEdit(item)}><BsPencil /></Button>
                          <Button size="sm" variant={item.ativo ? "outline-danger" : "outline-success"} aria-label={`${item.ativo ? "Desativar" : "Ativar"} ${item.nome}`} onClick={() => toggleActive(item)}>
                            {item.ativo ? <BsToggleOff /> : <BsToggleOn />}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!items.length && <tr><td colSpan="4" className="text-center">Nenhuma natureza cadastrada.</td></tr>}
                </tbody>
              </Table>
            )}
          </section>
        </Container>
      </main>
    </>
  );
}
